<script setup>
import { ref, onBeforeMount, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";

import AppFooter from "@/examples/PageLayout/Footer.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ResetPassword from "./domain/reset-password";

const store = useStore();
const router = useRouter();
const route = useRoute();

const showPassword = ref(false);
const showConfirmPassword = ref(false);
const password = ref("");
const confirmPassword = ref("");
const errors = ref([]);
const loading = ref(false); 


// Manejar el envío del formulario
// funcion para validar la contraseña
const validatePassword = (password) => {
  const requirements = [
    { regex: /.{8,}/, message: "Debe tener al menos 8 caracteres." },
    { regex: /[A-Z]/, message: "Debe contener al menos una letra mayúscula." },
    { regex: /[a-z]/, message: "Debe contener al menos una letra minúscula." },
    { regex: /\d/, message: "Debe contener al menos un número." },
    { regex: /[^A-Za-z0-9]/, message: "Debe contener al menos un caracter especial." },
  ];
  
  return requirements.filter(req => !req.regex.test(password)).map(req => req.message);
};

// Update the handleSubmit function to include password validation
const handleSubmit = async () => {
  errors.value = [];
  loading.value = true;  

  // Validate password requirements
  errors.value = validatePassword(password.value);
  if (errors.value.length > 0) {
    loading.value = false;
    return;
  }

  // Valida si las contraseñas coinciden
  if (password.value !== confirmPassword.value) {
    errors.value.push("Las contraseñas no coinciden.");
    loading.value = false;  
    return;
  }

  try {
    const token = route.params.token; 
    const response = await ResetPassword(token, password.value); // Envia el token y la nueva contraseña

    if (!response) {
      errors.value.push("No se recibió una respuesta al restablecer la contraseña. Inténtalo nuevamente.");
      loading.value = false;  
      return;
    }

    Swal.fire({
      icon: "success",
      title: "Contraseña restaurada exitosamente.",
      text: "Serás redirigido al inicio de sesión",
      customClass: {
        confirmButton: "btn bg-gradient-success",
      },
      buttonsStyling: false,
      timer: 3000, // 3 segundos
    }).then(() => {
      router.push({ name: "Login" });
    });

  } catch (error) {
    if (error.message.includes("400")) {
      errors.value.push('Link de recuperacion inválido o expirado');
    } else {
      errors.value.push('Error al conectar con el servidor: ' + (error.message));
    }
    loading.value = false;  
  }
};

// Validar que las contraseñas coincidan
watch([password, confirmPassword], () => {
  if (password.value !== confirmPassword.value && confirmPassword.value != "") {
    if (!errors.value.includes("Las contraseñas no coinciden.")) {
      errors.value.push("Las contraseñas no coinciden.");
    }
    invalidFields.value.confirmPassword = true;
  } else {
    errors.value = errors.value.filter(error => error !== "Las contraseñas no coinciden.");
    invalidFields.value.confirmPassword = false;
  }
});


const invalidFields = ref({});


</script>

<template>
  <main class="main-content mt-0">
    <div class="page-header align-items-start min-vh-50 pt-5 pb-11">
      <span class="mask bg-gradient-dark opacity-6"></span>
    </div>
    <div class="container">
      <div class="row mt-lg-n10 mt-md-n11 mt-n12 justify-content-center">
        <div class="col-xl-8 col-lg-7 col-md-9 col-sm-12 mx-auto">
          <div class="card mb-5">
            <div class="card-header text-center">
              <h4 class="font-weight-bolder">Restablecer contraseña</h4>
              <p class="mb-0">Introduce tu nueva contraseña.</p>
            </div>

            <div class="card-body">
              <form @submit.prevent="handleSubmit">
                <div class="d-flex flex-column flex-md-row justify-content-between">
                  <div class="w-100 w-md-40 pe-2">
                    <div class="position-relative">
                      <argon-input
                        id="password"
                        v-model="password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="Nueva Contraseña"
                        aria-label="password"
                        required
                      />
                      <span class="toggle-password" @click="showPassword = !showPassword">
                        <i :class="showPassword ? 'fas fa-eye-slash bg-white' : 'far fa-eye bg-white'"></i>
                      </span>
                    </div>

                    <div class="position-relative pt-4">
                      <argon-input
                        id="confirmPassword"
                        v-model="confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        placeholder="Confirmar Contraseña"
                        aria-label="confirmPassword"
                        required
                      />
                      <span class="toggle-password pt-4" @click="showConfirmPassword = !showConfirmPassword">
                        <i :class="showConfirmPassword ? 'fas fa-eye-slash bg-white' : 'far fa-eye bg-white'"></i>
                      </span>
                    </div>
                  </div>

                  <div class="w-100 w-md-60 ps-md-6 mt-3 mt-md-0">
                    <small class="text-muted">La contraseña debe contener:</small>
                    <div class="row list-row w-100">
                      <div class="col-lg-6 col-12 ps-0">
                        <ul class="password-requirements">
                          <li>8 caracteres como mínimo</li>
                          <li>Una letra mayúscula.</li>
                          <li>Una letra minúscula.</li>
                        </ul>
                      </div>
                      <div class="col-lg-6 col-12 ps-0">
                        <ul class="password-requirements">
                          <li>Un número.</li>
                          <li>Un carácter especial (ej. !, @, #, etc.).</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="errors.length" class="error-messages">
                  <ul>
                    <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                  </ul>
                </div>

                <div class="text-center">
                  <argon-button
                    type="submit"
                    color="dark"
                    full-width
                    variant="gradient"
                    size="lg"
                    class="mt-3 mb-0"
                    :disabled="loading"
                  >
                    {{ loading ? "Procesando..." : "Restablecer Contraseña" }}
                  </argon-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
.error-messages {
  color: red;
  margin-bottom: 15px;
}

.page-header {
  background-image: url('@/assets/img/loginbg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.toggle-password {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}

.password-requirements {
  list-style-type: none;
  padding-left: 15px;
}

.password-requirements li {
  font-size: 0.75rem;
  color: #6c757d;
  display: flex;
  align-items: center;
}

.password-requirements li::before {
  content: "•";
  color: #2dce89;
  font-size: 1.2rem;
  margin-right: 8px;
  display: inline-block;
}

@media (max-width: 768px) {
  .page-header {
    min-height: 30vh;
    padding-top: 3rem;
    padding-bottom: 5rem;
  }

  .card-header h4 {
    font-size: 1.2rem;
  }

  .card-header p {
    font-size: 0.9rem;
  }

  .password-requirements li {
    font-size: 0.7rem;
  }

  ul  {
    margin-bottom: 0rem !important;
  }

  .toggle-password {
    font-size: 1rem;
  }
}
</style>
